import * as React from 'react'
import { useState } from 'react'

import { Tooltip } from '../../../module'
import {
  type ButtonProps,
  isAnchorProps,
  isButtonProps,
  isLinkProps,
} from '../Button.models'
import styles from '../_button.module.scss'
import ConfirmationButton from './ConfirmationButton'
import { c } from '../../../translations/LibraryTranslationService'

/**
 * Need to add the following prop:
 * fullWidth?: boolean // Optionally make the button expand to the width of its parent container
 */
export function ButtonWrapper({
  as = 'button',
  className = '',
  styleType = 'secondary',
  disabled = false,
  tooltip,
  confirmation,
  popoverProps,
  routerComponent,
  destructive = false,
  qaTestId = 'button',
  ...rest
}: ButtonProps): React.JSX.Element {
  const notPrimary = !styleType.includes('primary')
  const allClassNames = `${styles[styleType]} ${styles.button} ${
    notPrimary ? styles.nonPrimaryFont : styles.primaryFont
  } ${destructive ? styles.destructive : ''} ${className}`

  const RouterComponent = routerComponent

  const [isTooltipOpen, setIsTooltipOpen] = useState(true)

  const renderComponentWithTooltip = (children: React.ReactNode) =>
    isTooltipOpen ? (
      <Tooltip
        tooltipContent={tooltip?.tooltipContent}
        position={tooltip?.position ?? 'bottom'}
      >
        {children}
      </Tooltip>
    ) : (
      children
    )

  if (as === 'link' && RouterComponent && isLinkProps(rest)) {
    return (
      <>
        {tooltip?.tooltipContent ? (
          renderComponentWithTooltip(
            <RouterComponent
              className={`${allClassNames} ${styles.link} ${disabled ? styles.disabled : ''}`}
              {...rest}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                setIsTooltipOpen(false)
                disabled && e.preventDefault()
              }}
              onMouseLeave={() => {
                setIsTooltipOpen(true)
              }}
              qa-test-id={qaTestId}
            />,
          )
        ) : (
          <RouterComponent
            qa-test-id={qaTestId}
            className={`${allClassNames} ${styles.link} ${disabled ? styles.disabled : ''}`}
            {...rest}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
              disabled && e.preventDefault()
            }
          />
        )}
      </>
    )
  } else if (as === 'externalLink' && isAnchorProps(rest)) {
    return (
      <>
        {tooltip?.tooltipContent ? (
          renderComponentWithTooltip(
            <a
              aria-disabled={disabled}
              className={`${allClassNames} ${styles.link} ${disabled ? styles.disabled : ''}`}
              // provide good + secure defaults while still allowing them to be overwritten
              target='_blank'
              rel='noopener noreferrer'
              {...rest}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                if (disabled) {
                  e.preventDefault()
                  return
                }
                rest.onClick && rest.onClick(e)
                setIsTooltipOpen(false)
              }}
              onMouseLeave={() => {
                setIsTooltipOpen(true)
              }}
              qa-test-id={qaTestId}
            >
              {rest.children}
            </a>,
          )
        ) : (
          <a
            className={`${allClassNames} ${styles.link} ${disabled ? styles.disabled : ''}`}
            // provide good + secure defaults while still allowing them to be overwritten
            target='_blank'
            rel='noopener noreferrer'
            aria-disabled={disabled}
            {...rest}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              if (disabled) {
                e.preventDefault()
                return
              }
              rest.onClick && rest.onClick(e)
            }}
            qa-test-id={qaTestId}
          >
            {rest.children}
          </a>
        )}
      </>
    )
  } else if (as === 'unstyled' && isButtonProps(rest)) {
    return (
      <>
        {tooltip?.tooltipContent ? (
          renderComponentWithTooltip(
            <button
              disabled={disabled}
              className={`${allClassNames} ${styles.unstyled} ${disabled ? styles.disabled : ''}`}
              {...rest}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                rest.onClick && rest.onClick(e)
                setIsTooltipOpen(false)
              }}
              onMouseLeave={() => {
                setIsTooltipOpen(true)
              }}
              qa-test-id={qaTestId}
            />,
          )
        ) : (
          <button
            className={`${styles.unstyled} ${className}`}
            disabled={disabled}
            {...rest}
            qa-test-id={qaTestId}
          />
        )}
      </>
    )
  } else if (as === 'confirmation' && confirmation) {
    return (
      <ConfirmationButton
        confirmation={confirmation}
        buttonProps={{
          children: rest.children,
          disabled,
          className: `${allClassNames} ${disabled ? styles.disabled : ''}`,
          tooltip,
        }}
        popoverProps={{
          ...popoverProps,
          position: rest.icon ? 'top' : popoverProps?.position,
        }}
        tooltip={tooltip}
        qaTestId={qaTestId}
      />
    )
  } else if (isButtonProps(rest)) {
    return (
      <>
        {tooltip?.tooltipContent ? (
          renderComponentWithTooltip(
            <button
              disabled={disabled}
              className={`${allClassNames} ${disabled ? styles.disabled : ''}`}
              {...rest}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                rest.onClick && rest.onClick(e)
                // Skip hiding tooltip for components with onMouseOver handlers
                // ButtonGroup component handles its own tooltip visibility logic
                if (!rest.onMouseOver) {
                  setIsTooltipOpen(false)
                }
              }}
              onMouseLeave={() => {
                !rest.onMouseOver && setIsTooltipOpen(true)
              }}
              qa-test-id={qaTestId}
            />,
          )
        ) : (
          <button
            disabled={disabled}
            className={allClassNames}
            {...rest}
            qa-test-id={qaTestId}
          />
        )}
      </>
    )
  }

  throw new Error(c('errorButtonWrapper'))
}
